import { useCallback, useEffect, useRef, useState } from "react"
import { Controller, useForm } from "react-hook-form"
import { toast } from "sonner"
import {
  ArrowUp,
  Check,
  ExternalLink,
  FileText,
  Loader2,
  Mic,
  PanelLeft,
  PanelRight,
  Paperclip,
  Square,
  X,
} from "lucide-react"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog"
import { cn } from "@/lib/utils"
import { RobinAvatar } from "./robin-avatar"
import { robinStateFor } from "./utils/robin-state"
import {
  useDictation,
  type DictationErrorCode,
} from "./hooks/use-dictation"
import { useRobinVoiceTranscription } from "./hooks/use-robin-voice-transcription"
import { ASSISTANT, HERO_SUGGESTIONS } from "./chat-copy"
import { ChoicesCard, FileAttachmentsList } from "./message-attachments"
import {
  COMPOSER_ATTACHMENT_ACCEPT,
  COMPOSER_ATTACHMENT_LIMIT,
  COMPOSER_ATTACHMENT_MAX_BYTES,
  composerAttachmentTypeLabel,
  draftMiniatureLineWidths,
  fileExtensionLabel,
  formatComposerBytes,
  isAllowedComposerFile,
  pdfDraftThumbnailUrl,
  totalDraftAttachmentBytes,
} from "./utils/composer-attachments"
import {
  CONVERSATION_STATUS_META,
  type RobinActiveAsk,
  type RobinConversation,
  type RobinMessage,
  type RobinMutationReceipt,
} from "./robin-types"

export type ChatPaneActions = {
  onSend: (text: string, files?: File[]) => void
  onChoose: (attachmentId: string, choiceId: string) => void
  onOpenReceipt?: (receipt: RobinMutationReceipt) => void
}

type DraftAttachment = {
  id: string
  file: File
  previewUrl?: string
}

type ComposerFormValues = {
  message: string
}

export function ChatPane({
  conv,
  actions,
  onOpenSidebar,
  onOpenApp,
  appOpen,
}: {
  conv: RobinConversation
  actions: ChatPaneActions
  onOpenSidebar: () => void
  /** Manually summon the app pane. */
  onOpenApp?: () => void
  appOpen?: boolean
}) {
  const isEmpty = conv.messages.length === 0 && !conv.pending
  const [dictating, setDictating] = useState(false)
  const headerState = dictating ? "listening" : robinStateFor(conv)

  return (
    <div className="flex h-full min-w-0 flex-1 flex-col">
      <header className="flex h-[52px] shrink-0 items-center gap-2.5 border-b px-3 md:px-[18px]">
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0 text-muted-foreground hover:bg-foreground/[0.04] hover:text-foreground md:hidden"
          onClick={onOpenSidebar}
          aria-label="Open conversations"
        >
          <PanelLeft />
        </Button>
        <RobinAvatar state={headerState} className="h-[26px] w-[26px]" />
        <h1 className="min-w-0 flex-1 truncate text-[13px] font-semibold">{conv.title}</h1>
        <div className="ml-auto flex shrink-0 items-center gap-1">
          <HeaderStatus conv={conv} />
          {onOpenApp && (
            <Button
              variant="ghost"
              size="icon"
              className={cn(
                "h-8 w-8 text-muted-foreground hover:bg-foreground/[0.04] hover:text-foreground",
                appOpen && "bg-foreground/[0.06] text-foreground",
              )}
              onClick={onOpenApp}
              title={appOpen ? "App panel open" : "Open app panel"}
              aria-label="Open app panel"
            >
              <PanelRight />
            </Button>
          )}
        </div>
      </header>

      {isEmpty ? (
        <EmptyChat onSend={actions.onSend} />
      ) : (
        <Transcript conv={conv} actions={actions} />
      )}

      <Composer
        disabled={Boolean(conv.pending) || conv.status === "working"}
        activeAsk={conv.activeAsk ?? null}
        onSend={actions.onSend}
        onChoose={(choiceId) => {
          if (conv.activeAsk) actions.onChoose(conv.activeAsk.askUserToolCallId, choiceId)
        }}
        autoFocusKey={conv.id}
        onDictatingChange={setDictating}
      />
    </div>
  )
}

function HeaderStatus({ conv }: { conv: RobinConversation }) {
  if (conv.status !== "working" && conv.status !== "needs_you" && conv.status !== "failed") {
    return null
  }
  const meta = CONVERSATION_STATUS_META[conv.status]
  return (
    <span className="flex shrink-0 items-center gap-1.5 px-1 text-[11px] text-muted-foreground">
      <span
        className={cn(
          "h-1.5 w-1.5 rounded-full",
          meta.dotClass,
          meta.pulse && "animate-pulse",
        )}
      />
      {meta.label}
    </span>
  )
}

function Transcript({
  conv,
  actions,
}: {
  conv: RobinConversation
  actions: ChatPaneActions
}) {
  const bottomRef = useRef<HTMLDivElement>(null)
  const lastConvId = useRef(conv.id)
  const last = conv.messages[conv.messages.length - 1]
  const midStream = last != null && last.streamChars != null
  const streamSig = last ? `${last.streamChars ?? ""}` : ""

  useEffect(() => {
    const switched = lastConvId.current !== conv.id
    lastConvId.current = conv.id
    bottomRef.current?.scrollIntoView({
      behavior: switched || midStream || conv.pending ? "instant" : "smooth",
      block: "end",
    })
  }, [conv.id, conv.messages.length, conv.pending, streamSig, midStream])

  return (
    <div className="min-h-0 flex-1 overflow-y-auto">
      <div
        role="log"
        aria-label={`Conversation with ${ASSISTANT.name}`}
        className="flex w-full flex-col gap-[18px] p-[18px]"
      >
        {conv.messages.map((message) => (
          <MessageRow
            key={message.id}
            conversationId={conv.id}
            message={message}
            actions={actions}
          />
        ))}
        {conv.pending && <ThinkingTrace steps={conv.pending.steps} />}
        <div ref={bottomRef} />
      </div>
    </div>
  )
}

function MessageRow({
  conversationId,
  message,
  actions,
}: {
  conversationId: string
  message: RobinMessage
  actions: ChatPaneActions
}) {
  const hasFileAttachments = Boolean(message.fileAttachments?.length)
  const showUserText = Boolean(message.text?.trim()) && !isFileOnlyTranscript(message)

  if (message.role === "user") {
    return (
      <div className="flex justify-end duration-300 animate-in fade-in-0 slide-in-from-bottom-1">
        <div className="flex max-w-[78%] flex-col items-end">
          {showUserText && (
            <div className="max-w-full rounded-2xl rounded-br-md bg-foreground/[0.07] px-[15px] py-2.5">
              <p className="whitespace-pre-wrap text-[13px] leading-[1.6]">
                {message.text}
              </p>
            </div>
          )}
          {hasFileAttachments && (
            <FileAttachmentsList
              conversationId={conversationId}
              attachments={message.fileAttachments}
              align="end"
            />
          )}
        </div>
      </div>
    )
  }

  const streaming = message.streamChars != null
  const shownText = streaming
    ? (message.text ?? "").slice(0, message.streamChars)
    : message.text

  return (
    <div className="flex gap-[11px] duration-300 animate-in fade-in-0 slide-in-from-bottom-1">
      <RobinAvatar state={streaming ? "responding" : "idle"} className="h-[30px] w-[30px]" />
      <div className="min-w-0 flex-1">
        {(shownText || streaming) && (
          <p className="whitespace-pre-wrap text-pretty text-[13px] leading-[1.65]">
            {shownText}
            {streaming && <Caret />}
          </p>
        )}
        {message.receipt && !streaming && actions.onOpenReceipt && (
          <MutationReceiptCard
            receipt={message.receipt}
            onOpen={actions.onOpenReceipt}
          />
        )}
        {hasFileAttachments && (
          <FileAttachmentsList
            conversationId={conversationId}
            attachments={message.fileAttachments}
          />
        )}
        {message.attachment &&
          message.attachment.options.length > 0 &&
          (message.attachment.readOnly || message.attachment.resolved) && (
            <ChoicesCard
              attachment={message.attachment}
              onChoose={(choiceId) => actions.onChoose(message.attachment!.id, choiceId)}
            />
          )}
      </div>
    </div>
  )
}

/**
 * Backend file-only turns use a bracketed transcript placeholder. Once Robin
 * renders thumbnails, repeating those filenames as bubble text makes the chat
 * feel noisier than the user's actual action.
 */
function isFileOnlyTranscript(message: RobinMessage): boolean {
  if (!message.fileAttachments?.length) return false
  return /^\[Uploaded files?: .+\]$/.test(message.text?.trim() ?? "")
}

/**
 * What Robin actually did, in words. The backend receipt carries only a type and
 * a target — no description — so this is the most that can honestly be said
 * until the contract carries the change itself.
 */
function receiptLabel(receipt: RobinMutationReceipt): string {
  const target = receipt.target
  const subject = target?.candidateId
    ? "candidate"
    : target?.jobId
      ? "job"
      : target?.contactId
        ? "contact"
        : null

  if (receipt.type === "comment") {
    return subject ? `Added a comment on this ${subject}` : "Added a comment"
  }
  return subject ? `Created a task on this ${subject}` : "Created a task"
}

/**
 * The receipt card, from the canvas.
 *
 * This is the load-bearing element of the whole product: the thing that turns
 * "the assistant says it did something" into "here is the record, go look". It
 * was a small underlined text link that rendered for comments only — tasks
 * produced no receipt at all — which gave the strongest idea in the design the
 * least visual weight on the page.
 */
function MutationReceiptCard({
  receipt,
  onOpen,
}: {
  receipt: RobinMutationReceipt
  onOpen: (receipt: RobinMutationReceipt) => void
}) {
  return (
    <button
      type="button"
      onClick={() => onOpen(receipt)}
      className="mt-[11px] flex w-full items-center gap-2.5 rounded-md border bg-card px-[13px] py-[11px] text-left transition-colors hover:border-foreground/20"
    >
      <Check className="h-[15px] w-[15px] shrink-0 text-brand-ink" />
      <span className="min-w-0 flex-1 text-xs leading-[1.4]">
        {receiptLabel(receipt)}
      </span>
      <ExternalLink className="h-[13px] w-[13px] shrink-0 text-muted-foreground" />
    </button>
  )
}

function Caret() {
  return (
    <span className="ml-px inline-block h-[1.05em] w-[2px] translate-y-[2px] rounded-full bg-foreground/70 align-middle motion-safe:animate-pulse" />
  )
}

function ThinkingTrace({ steps }: { steps: string[] }) {
  return (
    <div
      className="flex gap-[11px] duration-300 animate-in fade-in-0"
      aria-live="polite"
    >
      <RobinAvatar state="thinking" className="h-[30px] w-[30px]" />
      <div className="flex min-w-0 flex-1 flex-col gap-1.5 pt-1">
        {steps.map((step, i) => {
          const active = i === steps.length - 1
          return (
            <div
              key={`${step}-${i}`}
              className={cn(
                "flex items-center gap-2 text-[12.5px] leading-[1.4] duration-300 animate-in fade-in-0 slide-in-from-bottom-1",
                active ? "text-foreground" : "text-muted-foreground",
              )}
            >
              {active ? (
                <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-brand-ink" />
              ) : (
                <Check className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              )}
              <span className="min-w-0 truncate">{step}</span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function EmptyChat({ onSend }: { onSend: (text: string, files?: File[]) => void }) {
  return (
    <div className="min-h-0 flex-1 overflow-y-auto">
      <div className="mx-auto flex h-full w-full max-w-2xl flex-col items-center justify-center px-4 py-10">
        <RobinAvatar state="idle" className="h-14 w-14" />
        <h2 className="mt-4 text-center text-2xl font-semibold tracking-tight">
          Hi, I'm {ASSISTANT.name}.
        </h2>
        <p className="mt-1.5 max-w-md text-center text-sm text-muted-foreground">
          Ask about a candidate, a job, or your numbers - or hand me the work.
        </p>
        <div className="mt-7 flex w-full max-w-sm flex-col">
          {HERO_SUGGESTIONS.slice(0, 4).map((suggestion) => {
            const Icon = suggestion.icon
            return (
              <button
                key={suggestion.text}
                type="button"
                onClick={() => onSend(suggestion.text)}
                className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm text-muted-foreground transition-colors hover:bg-foreground/[0.04] hover:text-foreground"
              >
                <Icon className="h-4 w-4 shrink-0 text-muted-foreground/70" />
                <span className="min-w-0 truncate">{suggestion.text}</span>
              </button>
            )
          })}
        </div>
      </div>
    </div>
  )
}

function describeDictationError(code: DictationErrorCode): string {
  switch (code) {
    case "not-supported":
      return "Voice input is not supported in this browser."
    case "not-allowed":
      return "Microphone access is blocked - allow it in your browser to use voice."
    case "audio-capture":
      return "No microphone found - check your device, then tap the mic again."
    case "no-speech":
      return "Didn't catch that - tap the mic and try again."
    case "too-large":
      return "Voice notes can be up to 60 seconds or 10MB."
    case "not-authenticated":
      return "Sign in again to use voice transcription."
    case "timeout":
      return "Voice transcription took too long - try a shorter voice note."
    case "network":
      return "Voice transcription needs a connection - check your network and retry."
    case "transcription-failed":
      return "Voice transcription failed - tap the mic to try again."
    default:
      return "Voice stopped unexpectedly - tap the mic to try again."
  }
}

function Composer({
  disabled,
  activeAsk,
  onSend,
  onChoose,
  autoFocusKey,
  onDictatingChange,
}: {
  disabled: boolean
  activeAsk: RobinActiveAsk | null
  onSend: (text: string, files?: File[]) => void
  onChoose: (choiceId: string) => void
  autoFocusKey: string
  onDictatingChange?: (listening: boolean) => void
}) {
  const { control, getValues, handleSubmit, reset, setValue, watch } =
    useForm<ComposerFormValues>({
      defaultValues: { message: "" },
    })
  const text = watch("message")
  const [draftAttachments, setDraftAttachments] = useState<DraftAttachment[]>([])
  const [draftPreview, setDraftPreview] = useState<DraftAttachment | null>(null)
  const [attachmentError, setAttachmentError] = useState<string | null>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const lastVoiceErrorToastRef = useRef<DictationErrorCode | null>(null)
  const draftAttachmentsRef = useRef<DraftAttachment[]>([])
  draftAttachmentsRef.current = draftAttachments

  const clearDraftAttachments = useCallback(() => {
    draftAttachmentsRef.current.forEach(revokeDraftAttachment)
    draftAttachmentsRef.current = []
    setDraftAttachments([])
    setDraftPreview(null)
  }, [])

  const resize = useCallback(() => {
    const el = textareaRef.current
    if (!el) return
    el.style.height = "auto"
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`
  }, [])

  const transcribeAudio = useRobinVoiceTranscription()

  const setComposerText = useCallback(
    (nextText: string) => {
      setValue("message", nextText, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      })
      requestAnimationFrame(() => {
        resize()
        textareaRef.current?.focus()
      })
    },
    [resize, setValue],
  )

  const handleTranscript = useCallback(
    (transcript: string) => {
      setComposerText(appendComposerTranscript(getValues("message"), transcript))
    },
    [getValues, setComposerText],
  )

  const dictation = useDictation({
    onTranscript: handleTranscript,
    transcribeAudio,
  })
  const abortDictation = dictation.abort
  const voiceBusy = dictation.recording || dictation.transcribing
  const recordingInput = dictation.recording
  const voiceError = dictation.error
  const hasDraftText = text.trim().length > 0
  const showVoiceTextDraft =
    hasDraftText && (recordingInput || dictation.transcribing)

  useEffect(() => {
    onDictatingChange?.(recordingInput)
  }, [onDictatingChange, recordingInput])

  useEffect(() => {
    if (!voiceError) {
      lastVoiceErrorToastRef.current = null
      return
    }

    if (voiceBusy || lastVoiceErrorToastRef.current === voiceError) return

    lastVoiceErrorToastRef.current = voiceError
    toast.error("Voice input failed", {
      description: describeDictationError(voiceError),
      id: "robin-voice-input-error",
    })
  }, [voiceBusy, voiceError])

  const choiceAsk = activeAsk && activeAsk.options.length > 0 ? activeAsk : null
  const showingChoices = choiceAsk != null
  const canAttachFiles = !disabled && activeAsk == null && !voiceBusy

  useEffect(() => {
    if (
      !showingChoices &&
      !voiceBusy &&
      window.matchMedia("(min-width: 768px)").matches
    ) {
      textareaRef.current?.focus()
    }
  }, [autoFocusKey, showingChoices, voiceBusy])

  useEffect(() => {
    abortDictation()
    clearDraftAttachments()
    setAttachmentError(null)
  }, [abortDictation, autoFocusKey, clearDraftAttachments])

  useEffect(() => {
    if (!showingChoices) return
    abortDictation()
  }, [abortDictation, showingChoices])

  useEffect(() => {
    if (voiceBusy) return
    const frame = requestAnimationFrame(resize)
    return () => cancelAnimationFrame(frame)
  }, [resize, text, voiceBusy])

  useEffect(
    () => () => {
      draftAttachmentsRef.current.forEach(revokeDraftAttachment)
    },
    [],
  )

  const appendFiles = (files: File[]) => {
    if (files.length === 0) return
    if (!canAttachFiles) {
      setAttachmentError(
        voiceBusy
          ? "Finish voice input before attaching files."
          : "Answer Robin before attaching files.",
      )
      return
    }

    const nextAttachments = [...draftAttachments]
    let nextTotalBytes = totalDraftAttachmentBytes(nextAttachments)
    let nextError: string | null = null

    for (const file of files) {
      if (nextAttachments.length >= COMPOSER_ATTACHMENT_LIMIT) {
        nextError = `Attach up to ${COMPOSER_ATTACHMENT_LIMIT} files.`
        break
      }
      if (!isAllowedComposerFile(file)) {
        nextError = `${file.name || "This file"} is not supported.`
        continue
      }
      if (file.size <= 0) {
        nextError = `${file.name || "This file"} is empty.`
        continue
      }
      if (nextTotalBytes + file.size > COMPOSER_ATTACHMENT_MAX_BYTES) {
        nextError = `Attachments can be up to ${formatComposerBytes(COMPOSER_ATTACHMENT_MAX_BYTES)} total.`
        continue
      }

      nextAttachments.push(createDraftAttachment(file))
      nextTotalBytes += file.size
    }

    setDraftAttachments(nextAttachments)
    setAttachmentError(nextError)
  }

  const removeDraftAttachment = (attachment: DraftAttachment) => {
    revokeDraftAttachment(attachment)
    setDraftAttachments((current) =>
      current.filter((item) => item.id !== attachment.id),
    )
    if (draftPreview?.id === attachment.id) setDraftPreview(null)
  }

  const canSend =
    !showingChoices &&
    !disabled &&
    !voiceBusy &&
    !(activeAsk && draftAttachments.length > 0) &&
    (text.trim().length > 0 || draftAttachments.length > 0)

  const submit = handleSubmit(({ message }) => {
    if (!canSend) return
    const files = draftAttachments.map((attachment) => attachment.file)
    onSend(message, files)
    draftAttachments.forEach(revokeDraftAttachment)
    setDraftAttachments([])
    setDraftPreview(null)
    setAttachmentError(null)
    reset({ message: "" })
    const el = textareaRef.current
    if (el) el.style.height = "auto"
  })

  const footerText = choiceAsk
    ? "Choose an option to continue."
    : dictation.transcribing
      ? "Transcribing voice..."
      : recordingInput
        ? dictation.listening
          ? "Recording - click the wave or square to stop."
          : "Starting microphone..."
        : attachmentError
          ? attachmentError
          : voiceError
            ? describeDictationError(voiceError)
            : `${ASSISTANT.name} checks with you before changing anything in your ATS.`

  const renderMessageTextarea = (placeholder?: string) => (
    <Controller
      control={control}
      name="message"
      render={({ field }) => (
        <textarea
          {...field}
          ref={(node) => {
            field.ref(node)
            textareaRef.current = node
          }}
          rows={1}
          placeholder={placeholder}
          onChange={(event) => {
            field.onChange(event)
            resize()
          }}
          onPaste={(event) => {
            const pastedFiles = filesFromClipboard(event.clipboardData)
            if (pastedFiles.length === 0) return
            event.preventDefault()
            appendFiles(pastedFiles)
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter" && !event.shiftKey) {
              event.preventDefault()
              void submit()
            }
          }}
          className="block max-h-40 w-full min-w-0 resize-none bg-transparent py-1.5 text-sm leading-relaxed outline-none placeholder:text-muted-foreground"
        />
      )}
    />
  )

  return (
    <div className="shrink-0 border-t px-[18px] pb-4 pt-3">
      <div className="w-full">
        <div
          className={cn(
            // Canvas: a 22px pill on a surface, padded tighter on the right
            // where the two 34px controls sit.
            "rounded-[22px] border bg-card py-2 pl-[15px] pr-2 transition-colors focus-within:border-ring/50",
            voiceBusy && "border-attention/60 ring-1 ring-attention/30",
          )}
        >
          {choiceAsk ? (
            <div className="w-full space-y-2 py-1">
              <p className="px-1 text-xs font-medium text-muted-foreground">
                Choose one to continue
              </p>
              <div className="grid gap-1.5">
                {choiceAsk.options.map((option) => (
                  <Button
                    key={option.id}
                    type="button"
                    variant="ghost"
                    disabled={disabled}
                    onClick={() => onChoose(option.id)}
                    className="h-auto w-full justify-start rounded-2xl px-3 py-2 text-left whitespace-normal"
                  >
                    <span className="min-w-0">
                      <span className="block truncate text-sm">{option.name}</span>
                      <span className="block truncate text-xs text-muted-foreground">
                        {option.meta}
                      </span>
                    </span>
                  </Button>
                ))}
              </div>
            </div>
          ) : (
            <>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept={COMPOSER_ATTACHMENT_ACCEPT}
                className="sr-only"
                onChange={(event) => {
                  appendFiles(Array.from(event.target.files ?? []))
                  event.target.value = ""
                }}
              />
              {draftAttachments.length > 0 && (
                <div className="flex flex-wrap gap-1.5 px-1 pb-1">
                  {draftAttachments.map((attachment) => (
                    <DraftAttachmentThumbnail
                      key={attachment.id}
                      attachment={attachment}
                      onOpen={() => setDraftPreview(attachment)}
                      onRemove={() => removeDraftAttachment(attachment)}
                    />
                  ))}
                </div>
              )}
              <div
                className={cn(
                  "flex gap-1",
                  recordingInput && !hasDraftText ? "items-center" : "items-end",
                )}
              >
                {recordingInput ? (
                  <button
                    type="button"
                    onClick={dictation.abort}
                    aria-label="Cancel voice recording"
                    title="Cancel recording"
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-foreground/[0.06] text-muted-foreground transition-colors hover:bg-foreground/[0.1] hover:text-foreground"
                  >
                    <X className="h-[18px] w-[18px]" />
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={!canAttachFiles}
                    aria-label="Attach files"
                    title="Attach files"
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-foreground/[0.06] hover:text-foreground disabled:opacity-40"
                  >
                    <Paperclip className="h-[18px] w-[18px]" />
                  </button>
                )}
                <div
                  className={cn(
                    "min-w-0 flex-1",
                    showVoiceTextDraft && "space-y-1",
                  )}
                >
                  {showVoiceTextDraft && renderMessageTextarea()}
                  {dictation.recording ? (
                    <VoiceRecordingSurface
                      waveformContainerRef={dictation.waveformContainerRef}
                      hasText={hasDraftText}
                      onStop={dictation.stop}
                    />
                  ) : dictation.transcribing ? (
                    <VoiceTranscribingSurface />
                  ) : (
                    renderMessageTextarea(`Message ${ASSISTANT.name}...`)
                  )}
                </div>
                {dictation.supported && !voiceBusy && (
                  <button
                    type="button"
                    onClick={() => {
                      textareaRef.current?.focus()
                      dictation.start()
                    }}
                    disabled={disabled}
                    aria-label="Dictate a message"
                    title="Dictate"
                    className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-foreground/[0.06] hover:text-foreground disabled:opacity-40"
                  >
                    <Mic className="h-4 w-4" />
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => void submit()}
                  disabled={!canSend}
                  aria-label="Send message"
                  className={cn(
                    "flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full transition-colors",
                    canSend
                      ? "bg-primary text-primary-foreground"
                      : "bg-foreground/[0.06] text-muted-foreground/50",
                  )}
                >
                  <ArrowUp className="h-4 w-4" />
                </button>
              </div>
              <DraftAttachmentPreviewDialog
                attachment={draftPreview}
                onOpenChange={(open) => {
                  if (!open) setDraftPreview(null)
                }}
              />
            </>
          )}
        </div>
        <p
          className={cn(
            "mt-2 text-center text-[11px]",
            !choiceAsk && !voiceBusy && (attachmentError || voiceError)
              ? "text-foreground/70"
              : "text-muted-foreground",
          )}
        >
          {footerText}
        </p>
      </div>
    </div>
  )
}

function VoiceRecordingSurface({
  waveformContainerRef,
  hasText,
  onStop,
}: {
  waveformContainerRef: (node: HTMLDivElement | null) => void
  hasText: boolean
  onStop: () => void
}) {
  return (
    <button
      type="button"
      onClick={onStop}
      aria-label="Stop recording and transcribe"
      title="Stop recording"
      className={cn(
        "flex w-full min-w-0 items-center gap-3 rounded-full text-muted-foreground transition-colors hover:bg-foreground/[0.04]",
        hasText ? "h-9 px-1" : "min-h-[72px] px-2 py-3",
      )}
    >
      <div
        ref={waveformContainerRef}
        aria-hidden="true"
        className={cn(
          "block min-w-0 flex-1 overflow-hidden rounded-full",
          hasText ? "h-7" : "h-12",
        )}
      />
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-foreground/[0.08] text-foreground">
        <Square className="h-3 w-3 fill-current" />
      </span>
    </button>
  )
}

function VoiceTranscribingSurface() {
  return (
    <div className="flex h-8 w-full min-w-0 items-center justify-center gap-2 rounded-full bg-foreground/[0.04] px-3 text-muted-foreground">
      <Loader2 className="h-3.5 w-3.5 animate-spin" />
      <span className="text-xs font-medium">Transcribing</span>
    </div>
  )
}

function appendComposerTranscript(current: string, transcript: string): string {
  const trimmedTranscript = transcript.trim()
  if (!trimmedTranscript) return current

  const base = current.trimEnd()
  if (!base) return trimmedTranscript

  return `${base} ${trimmedTranscript}`
}

function DraftAttachmentThumbnail({
  attachment,
  onOpen,
  onRemove,
}: {
  attachment: DraftAttachment
  onOpen: () => void
  onRemove: () => void
}) {
  const previewUrl = attachment.previewUrl
  const isImage = attachment.file.type.startsWith("image/") && previewUrl
  const isPdf = attachment.file.type === "application/pdf" && previewUrl

  return (
    <div className="group relative h-16 w-16 overflow-hidden rounded-lg border border-border/70 bg-background shadow-sm">
      <button
        type="button"
        onClick={onOpen}
        title={attachment.file.name}
        aria-label={`Preview ${attachment.file.name}`}
        className="h-full w-full focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
      >
        {isImage ? (
          <img
            src={previewUrl}
            alt=""
            draggable={false}
            className="h-full w-full object-cover"
          />
        ) : isPdf ? (
          <DraftPdfThumbnailSurface url={previewUrl} />
        ) : (
          <DraftDocumentThumbnailSurface label={fileExtensionLabel(attachment.file)} />
        )}
      </button>
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove ${attachment.file.name}`}
        className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-background/95 text-muted-foreground opacity-0 shadow-sm transition-opacity hover:text-foreground focus:opacity-100 group-hover:opacity-100"
      >
        <X className="h-3.5 w-3.5" />
      </button>
      {!isImage && (
        <span className="absolute bottom-1 right-1 rounded bg-background/90 px-1 text-[10px] font-semibold leading-4 text-foreground shadow-sm">
          {fileExtensionLabel(attachment.file)}
        </span>
      )}
    </div>
  )
}

function DraftPdfThumbnailSurface({ url }: { url: string }) {
  return (
    <span className="block h-full w-full overflow-hidden bg-white">
      <iframe
        title=""
        src={pdfDraftThumbnailUrl(url)}
        tabIndex={-1}
        className="pointer-events-none h-[220%] w-[220%] origin-top-left scale-[0.46] border-0 bg-white"
      />
    </span>
  )
}

function DraftDocumentThumbnailSurface({ label }: { label: string }) {
  const widths = draftMiniatureLineWidths(label)

  return (
    <span className="flex h-full w-full flex-col bg-background p-2 text-left">
      <span className="mb-1.5 h-1.5 w-7 rounded-full bg-foreground/25" />
      {widths.map((width, index) => (
        <span
          key={`${width}-${index}`}
          className="mb-1 h-1 rounded-full bg-muted-foreground/20"
          style={{ width: `${width}%` }}
        />
      ))}
    </span>
  )
}

function DraftAttachmentPreviewDialog({
  attachment,
  onOpenChange,
}: {
  attachment: DraftAttachment | null
  onOpenChange: (open: boolean) => void
}) {
  const fileUrl = attachment?.previewUrl

  return (
    <Dialog open={Boolean(attachment)} onOpenChange={onOpenChange}>
      {attachment && (
        <DialogContent className="flex h-[calc(100vh-2rem)] w-[calc(100vw-2rem)] max-w-none items-center justify-center border-0 bg-transparent p-0 shadow-none sm:rounded-none [&>button:last-child]:rounded-full [&>button:last-child]:bg-background/90 [&>button:last-child]:p-2 [&>button:last-child]:opacity-100 [&>button:last-child]:shadow-sm">
          <DialogTitle className="sr-only">{attachment.file.name}</DialogTitle>
          <DialogDescription className="sr-only">
            {composerAttachmentTypeLabel(attachment.file)},{" "}
            {formatComposerBytes(attachment.file.size)}
          </DialogDescription>
          {fileUrl && attachment.file.type.startsWith("image/") ? (
            <div className="flex h-full w-full items-center justify-center p-4">
              <img
                src={fileUrl}
                alt={attachment.file.name}
                className="max-h-full max-w-full rounded-lg object-contain shadow-2xl"
              />
            </div>
          ) : fileUrl && attachment.file.type === "application/pdf" ? (
            <iframe
              title={attachment.file.name}
              src={fileUrl}
              className="h-[min(86vh,900px)] w-[min(92vw,960px)] rounded-lg border-0 bg-background shadow-2xl"
            />
          ) : (
            <div className="w-[min(92vw,420px)] rounded-lg bg-background p-5 text-center shadow-2xl">
              <FileText className="mx-auto h-7 w-7 text-muted-foreground" />
              <p className="mt-3 truncate text-sm font-medium">{attachment.file.name}</p>
              <p className="mt-1 text-xs text-muted-foreground">
                {composerAttachmentTypeLabel(attachment.file)},{" "}
                {formatComposerBytes(attachment.file.size)}
              </p>
            </div>
          )}
          {fileUrl && (
            <a
              href={fileUrl}
              target="_blank"
              rel="noreferrer"
              className="absolute bottom-3 right-3 inline-flex h-9 items-center gap-2 rounded-full bg-background/90 px-3 text-xs font-medium text-foreground shadow-sm ring-1 ring-border/70 transition-colors hover:bg-background"
            >
              Open file
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
          )}
        </DialogContent>
      )}
    </Dialog>
  )
}

function createDraftAttachment(file: File): DraftAttachment {
  return {
    id: `${Date.now()}_${Math.random().toString(36).slice(2)}`,
    file,
    previewUrl: URL.createObjectURL(file),
  }
}

function filesFromClipboard(data: DataTransfer): File[] {
  const directFiles = Array.from(data.files)
  if (directFiles.length > 0) return directFiles

  return Array.from(data.items).flatMap((item) => {
    if (item.kind !== "file") return []
    const file = item.getAsFile()
    return file ? [file] : []
  })
}

function revokeDraftAttachment(attachment: DraftAttachment) {
  if (attachment.previewUrl) URL.revokeObjectURL(attachment.previewUrl)
}
